import React from "react";
import {
  $getSelection,
  $getRoot,
  $insertNodes,
  $isRangeSelection,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  type ElementFormatType,
  type EditorState,
  type LexicalEditor,
} from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { HeadingNode, QuoteNode, $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $getNearestBlockElementAncestorOrThrow } from "@lexical/utils";
import { BoldIcon, ItalicIcon, UnderlineIcon, AlignLeftIcon, AlignCenterIcon, AlignRightIcon, AlignJustifyIcon, ChevronDownIcon } from "lucide-react";

import { cn } from "@javascript/components/utils";
import { Button } from "@javascript/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@javascript/components/ui/popover";
import { Separator } from "@javascript/components/ui/separator";

const editorTheme = {
  paragraph: "mb-2 last:mb-0",
  text: { bold: "font-semibold", italic: "italic", underline: "underline" },
  quote: "border-l-2 border-border pl-3 italic text-muted-foreground",
  heading: { h1: "text-2xl font-semibold mb-2", h2: "text-xl font-semibold mb-2", h3: "text-lg font-semibold mb-2" },
};

function onError(error: Error) {
  throw error;
}

function InitialContentPlugin({ html }: { html: string }) {
  const [editor] = useLexicalComposerContext();
  const loaded = React.useRef(false);

  React.useEffect(() => {
    if (loaded.current || !html) return;
    loaded.current = true;

    editor.update(() => {
      const dom = new DOMParser().parseFromString(html, "text/html");
      const nodes = $generateNodesFromDOM(editor, dom);
      $getRoot().clear();
      $insertNodes(nodes);
    });
  }, [editor, html]);

  return null;
}

function ToolbarButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-sm"
      aria-label={label}
      aria-pressed={active}
      data-active={active}
      className="data-[active=true]:bg-accent data-[active=true]:text-accent-foreground"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

const BLOCK_TYPE_LABELS = {
  paragraph: "Normal",
  h1: "Heading 1",
  h2: "Heading 2",
  h3: "Heading 3",
  quote: "Quote",
} as const;

type BlockType = keyof typeof BLOCK_TYPE_LABELS;

function BlockTypeSelect() {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = React.useState<BlockType>("paragraph");

  React.useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const element = $getNearestBlockElementAncestorOrThrow(selection.anchor.getNode());
        if ($isHeadingNode(element)) {
          const tag = element.getTag();
          setBlockType(tag === "h1" || tag === "h2" || tag === "h3" ? tag : "paragraph");
        } else if ($isQuoteNode(element)) {
          setBlockType("quote");
        } else {
          setBlockType("paragraph");
        }
      });
    });
  }, [editor]);

  function handleChange(value: BlockType) {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (value === "quote") $setBlocksType(selection, () => $createQuoteNode());
      else if (value === "paragraph") $setBlocksType(selection, () => $createParagraphNode());
      else $setBlocksType(selection, () => $createHeadingNode(value));
    });
  }

  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Block type"
          className="h-8 gap-1 px-2 font-normal"
          onMouseDown={(event) => event.preventDefault()}
        >
          {BLOCK_TYPE_LABELS[blockType]}
          <ChevronDownIcon className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-40 p-1" onCloseAutoFocus={(event) => event.preventDefault()}>
        {Object.entries(BLOCK_TYPE_LABELS).map(([value, label]) => (
          <button
            key={value}
            type="button"
            role="option"
            aria-selected={blockType === value}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              handleChange(value as BlockType);
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground",
              blockType === value && "bg-accent text-accent-foreground",
            )}
          >
            {label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const ALIGNMENTS: { value: ElementFormatType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { value: "left", label: "Align left", icon: AlignLeftIcon },
  { value: "center", label: "Align center", icon: AlignCenterIcon },
  { value: "right", label: "Align right", icon: AlignRightIcon },
  { value: "justify", label: "Justify", icon: AlignJustifyIcon },
];

function Toolbar() {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = React.useState<Set<string>>(new Set());
  const [alignment, setAlignment] = React.useState<ElementFormatType>("left");

  React.useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const formats = new Set<string>();
        if (selection.hasFormat("bold")) formats.add("bold");
        if (selection.hasFormat("italic")) formats.add("italic");
        if (selection.hasFormat("underline")) formats.add("underline");
        setActiveFormats(formats);

        const element = $getNearestBlockElementAncestorOrThrow(selection.anchor.getNode());
        setAlignment(element.getFormatType() || "left");
      });
    });
  }, [editor]);

  return (
    <div className="flex items-center gap-1 border-b border-input p-1">
      <BlockTypeSelect />
      <Separator orientation="vertical" className="h-5" />
      <ToolbarButton active={activeFormats.has("bold")} label="Bold" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")}>
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton active={activeFormats.has("italic")} label="Italic" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")}>
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton active={activeFormats.has("underline")} label="Underline" onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")}>
        <UnderlineIcon />
      </ToolbarButton>
      <Separator orientation="vertical" className="h-5" />
      {ALIGNMENTS.map(({ value, label, icon: Icon }) => (
        <ToolbarButton key={value} active={alignment === value} label={label} onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, value)}>
          <Icon />
        </ToolbarButton>
      ))}
    </div>
  );
}

export function RichTextEditor({
  id,
  name,
  label,
  initialHtml,
  placeholder,
  invalid,
}: {
  id: string;
  name: string;
  label: string;
  initialHtml: string;
  placeholder?: string;
  invalid: boolean;
}) {
  const [html, setHtml] = React.useState(initialHtml);

  return (
    <LexicalComposer
      initialConfig={{
        namespace: "InvitationRichText",
        theme: editorTheme,
        onError,
        nodes: [HeadingNode, QuoteNode],
      }}
    >
      <div data-slot="rich-text-editor" className={cn("rounded-md border border-input shadow-xs", invalid && "border-destructive ring-destructive/20")}>
        <Toolbar />
        <div className="relative">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                aria-label={label}
                aria-invalid={invalid}
                className="min-h-40 px-3 py-2 text-base outline-none md:text-sm"
              />
            }
            placeholder={
              <div className="pointer-events-none absolute inset-0 px-3 py-2 text-base text-muted-foreground md:text-sm">
                {placeholder}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
      </div>
      <HistoryPlugin />
      <InitialContentPlugin html={initialHtml} />
      <OnChangePlugin
        onChange={(editorState: EditorState, editorInstance: LexicalEditor) => {
          editorState.read(() => setHtml($generateHtmlFromNodes(editorInstance)));
        }}
      />
      <input type="hidden" name={name} value={html} />
    </LexicalComposer>
  );
}
